import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';

export interface SessionRescheduleNotifyDialogProps {
  open: boolean;
  lessonName: string;
  requireReason?: boolean;
  showNotifyOption?: boolean;
  onConfirm: (notifyGuardians: boolean, reason?: string) => void;
  onCancel: () => void;
  loading?: boolean;
}

function SessionRescheduleNotifyDialogContent({
  lessonName,
  requireReason = false,
  showNotifyOption = true,
  onConfirm,
  onCancel,
  loading = false,
}: Omit<SessionRescheduleNotifyDialogProps, 'open'>) {
  const [notifyGuardians, setNotifyGuardians] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  const handleConfirm = () => {
    if (requireReason && !reason.trim()) {
      setReasonError('Please provide a reason for updating this past session');
      return;
    }
    onConfirm(notifyGuardians, reason.trim() || undefined);
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Save session update?</DialogTitle>
        <DialogDescription>
          The schedule for <strong>{lessonName}</strong> has changed. Save this update?
        </DialogDescription>
      </DialogHeader>
      {requireReason ? (
        <div className="space-y-1.5">
          <Label htmlFor="session-reschedule-reason" className="text-sm">
            Reason <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="session-reschedule-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (reasonError) setReasonError('');
            }}
            placeholder="Reason for updating a past session"
            rows={3}
            disabled={loading}
          />
          {reasonError ? (
            <p className="text-sm text-destructive" role="alert">
              {reasonError}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Required when updating a session that has already occurred.
            </p>
          )}
        </div>
      ) : null}
      {showNotifyOption ? (
        <div className="flex items-start gap-2 py-2">
          <Checkbox
            id="notify-guardians"
            checked={notifyGuardians}
            onCheckedChange={(checked) => setNotifyGuardians(checked === true)}
            disabled={loading}
          />
          <Label
            htmlFor="notify-guardians"
            className="text-sm font-normal leading-snug cursor-pointer"
          >
            Notify enrolled students and guardians about the new time
          </Label>
        </div>
      ) : null}
      <DialogFooter className="gap-2 sm:gap-0">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="button" onClick={handleConfirm} disabled={loading}>
          {loading ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

export function SessionRescheduleNotifyDialog({
  open,
  onCancel,
  ...contentProps
}: SessionRescheduleNotifyDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      {open ? <SessionRescheduleNotifyDialogContent onCancel={onCancel} {...contentProps} /> : null}
    </Dialog>
  );
}
